/**
 * Day Templates Service
 *
 * Manages day templates - collections of periods that form a school day.
 * Day templates are reusable patterns (e.g., "Normal Day", "Assembly Day", "Short Friday")
 * that can be referenced by timetables.
 *
 * Collection: schools/{schoolId}/dayTemplates
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// Cache for day templates
let cachedTemplates = null;
let cacheSchoolId = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Default day templates
const DEFAULT_TEMPLATES = [
  {
    id: 'normal-day',
    name: 'Normal Day',
    description: 'Standard school day schedule',
    color: '#3B82F6',
    periods: [
      { label: 'Period 1', startTime: '08:30', endTime: '09:20', type: 'class', order: 0 },
      { label: 'Period 2', startTime: '09:20', endTime: '10:10', type: 'class', order: 1 },
      { label: 'Break', startTime: '10:10', endTime: '10:30', type: 'break', order: 2 },
      { label: 'Period 3', startTime: '10:30', endTime: '11:20', type: 'class', order: 3 },
      { label: 'Period 4', startTime: '11:20', endTime: '12:10', type: 'class', order: 4 },
      { label: 'Lunch', startTime: '12:10', endTime: '12:50', type: 'break', order: 5 },
      { label: 'Period 5', startTime: '12:50', endTime: '13:40', type: 'class', order: 6 },
      { label: 'Period 6', startTime: '13:40', endTime: '14:30', type: 'class', order: 7 }
    ]
  }
];

class DayTemplatesService {
  constructor() {
    this.collectionName = 'dayTemplates';
    this.listeners = new Map();
  }

  /**
   * Get collection reference for a specific school
   */
  getCollectionRef(schoolId) {
    if (!schoolId) {
      throw new Error('School ID is required for day templates operations');
    }
    return collection(db, 'schools', schoolId, this.collectionName);
  }

  /**
   * Clear cache when school changes
   */
  clearCache() {
    cachedTemplates = null;
    cacheSchoolId = null;
    cacheTimestamp = null;
  }

  /**
   * Get all day templates for a school (with caching)
   */
  async getAll(schoolId) {
    if (!schoolId) {
      console.warn('No schoolId provided, returning default templates');
      return DEFAULT_TEMPLATES;
    }

    // Check cache
    if (cachedTemplates &&
        cacheSchoolId === schoolId &&
        cacheTimestamp &&
        Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedTemplates;
    }

    try {
      const collectionRef = this.getCollectionRef(schoolId);
      const q = query(collectionRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // No custom templates, return defaults
        return DEFAULT_TEMPLATES;
      }

      const templates = [];
      snapshot.forEach((doc) => {
        templates.push({ id: doc.id, ...doc.data() });
      });

      // Update cache
      cachedTemplates = templates;
      cacheSchoolId = schoolId;
      cacheTimestamp = Date.now();

      return templates;
    } catch (error) {
      console.error('Error fetching day templates:', error);
      return DEFAULT_TEMPLATES;
    }
  }

  /**
   * Get a single day template by ID
   */
  async getById(schoolId, templateId) {
    if (!schoolId || !templateId) {
      throw new Error('School ID and Template ID are required');
    }

    try {
      const docRef = doc(this.getCollectionRef(schoolId), templateId);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        return null;
      }

      return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
      console.error('Error fetching day template:', error);
      return null;
    }
  }

  /**
   * Subscribe to real-time updates
   */
  subscribe(schoolId, callback) {
    if (!schoolId) {
      console.warn('No schoolId provided for subscription');
      callback(DEFAULT_TEMPLATES);
      return () => {};
    }

    // Unsubscribe from previous listener if exists
    this.unsubscribe(schoolId);

    const collectionRef = this.getCollectionRef(schoolId);
    const q = query(collectionRef, orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        if (snapshot.empty) {
          callback(DEFAULT_TEMPLATES);
          return;
        }

        const templates = [];
        snapshot.forEach((doc) => {
          templates.push({ id: doc.id, ...doc.data() });
        });

        // Update cache
        cachedTemplates = templates;
        cacheSchoolId = schoolId;
        cacheTimestamp = Date.now();

        callback(templates);
      },
      (error) => {
        console.error('Error in day templates subscription:', error);
        callback(DEFAULT_TEMPLATES);
      }
    );

    this.listeners.set(schoolId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribe(schoolId) {
    const unsubscribe = this.listeners.get(schoolId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(schoolId);
    }
  }

  /**
   * Create or update a day template
   */
  async save(schoolId, templateData) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    // Validate template data
    const errors = this.validateTemplate(templateData);
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }

    const { id, ...data } = templateData;
    const templateId = id || this.generateTemplateId(data.name);

    // Add timestamps
    const now = new Date();
    if (!id) {
      data.createdAt = now;
    }
    data.updatedAt = now;

    // Calculate total duration
    data.duration = this.calculateDuration(data.periods);

    const docRef = doc(this.getCollectionRef(schoolId), templateId);
    await setDoc(docRef, data, { merge: true });

    // Clear cache
    this.clearCache();

    return { id: templateId, ...data };
  }

  /**
   * Delete a day template
   */
  async delete(schoolId, templateId) {
    if (!schoolId || !templateId) {
      throw new Error('School ID and Template ID are required');
    }

    const docRef = doc(this.getCollectionRef(schoolId), templateId);
    await deleteDoc(docRef);

    // Clear cache
    this.clearCache();
  }

  /**
   * Clone a day template
   */
  async clone(schoolId, templateId, newName) {
    const template = await this.getById(schoolId, templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    const clonedTemplate = {
      ...template,
      name: newName || `${template.name} (Copy)`,
      id: undefined // Will generate new ID
    };

    return this.save(schoolId, clonedTemplate);
  }

  /**
   * Bulk update templates (replace all)
   */
  async bulkUpdate(schoolId, templates) {
    if (!schoolId) {
      throw new Error('School ID is required');
    }

    const batch = writeBatch(db);
    const collectionRef = this.getCollectionRef(schoolId);

    // Delete existing templates
    const existingSnapshot = await getDocs(collectionRef);
    existingSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    // Add new templates
    const now = new Date();
    templates.forEach((template) => {
      const templateId = template.id || this.generateTemplateId(template.name);
      const docRef = doc(collectionRef, templateId);
      batch.set(docRef, {
        ...template,
        duration: this.calculateDuration(template.periods),
        createdAt: now,
        updatedAt: now
      });
    });

    await batch.commit();

    // Clear cache
    this.clearCache();
  }

  /**
   * Update sort orders for multiple templates (used by drag-and-drop)
   */
  async updateSortOrders(schoolId, updates) {
    if (!schoolId || !updates?.length) return;

    const batch = writeBatch(db);
    const collectionRef = this.getCollectionRef(schoolId);

    updates.forEach(({ id, sortOrder }) => {
      const docRef = doc(collectionRef, id);
      batch.update(docRef, { sortOrder, updatedAt: new Date() });
    });

    await batch.commit();
    this.clearCache();
  }

  /**
   * Reset to default templates
   */
  async resetToDefaults(schoolId) {
    return this.bulkUpdate(schoolId, DEFAULT_TEMPLATES);
  }

  /**
   * Get current period from a day template
   */
  getCurrentPeriod(template, time = new Date()) {
    if (!template || !template.periods) {
      return null;
    }

    const hours = time.getHours();
    const minutes = time.getMinutes();
    const currentMinutes = hours * 60 + minutes;

    for (const period of template.periods) {
      const [startHour, startMinute] = period.startTime.split(':').map(Number);
      const [endHour, endMinute] = period.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      if (currentMinutes >= startMinutes && currentMinutes <= endMinutes) {
        return period;
      }
    }

    return null;
  }

  /**
   * Validate template data
   */
  validateTemplate(template) {
    const errors = [];

    if (!template.name) {
      errors.push('Template name is required');
    }

    if (!template.periods || !Array.isArray(template.periods) || template.periods.length === 0) {
      errors.push('Template must have at least one period');
    }

    if (template.periods) {
      // Validate each period
      template.periods.forEach((period, index) => {
        if (!period.label) {
          errors.push(`Period ${index + 1}: Label is required`);
        }

        if (!period.startTime || !this.isValidTime(period.startTime)) {
          errors.push(`Period ${index + 1}: Valid start time (HH:MM) is required`);
        }

        if (!period.endTime || !this.isValidTime(period.endTime)) {
          errors.push(`Period ${index + 1}: Valid end time (HH:MM) is required`);
        }

        if (period.startTime && period.endTime) {
          const [startHour, startMinute] = period.startTime.split(':').map(Number);
          const [endHour, endMinute] = period.endTime.split(':').map(Number);
          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          if (startMinutes >= endMinutes) {
            errors.push(`Period ${index + 1}: End time must be after start time`);
          }
        }

        if (!period.type || !['class', 'break', 'non-class', 'assembly', 'admin', 'reading', 'tutorial', 'other'].includes(period.type)) {
          errors.push(`Period ${index + 1}: Invalid period type`);
        }
      });

      // Check for overlaps
      const overlaps = this.checkPeriodOverlaps(template.periods);
      if (overlaps.length > 0) {
        errors.push(...overlaps.map(o => o.message));
      }
    }

    if (!template.color || !this.isValidColor(template.color)) {
      errors.push('Valid color is required');
    }

    return errors;
  }

  /**
   * Check for period overlaps within a template
   */
  checkPeriodOverlaps(periods) {
    const overlaps = [];

    for (let i = 0; i < periods.length; i++) {
      for (let j = i + 1; j < periods.length; j++) {
        const period1 = periods[i];
        const period2 = periods[j];

        const [start1Hour, start1Minute] = period1.startTime.split(':').map(Number);
        const [end1Hour, end1Minute] = period1.endTime.split(':').map(Number);
        const [start2Hour, start2Minute] = period2.startTime.split(':').map(Number);
        const [end2Hour, end2Minute] = period2.endTime.split(':').map(Number);

        const start1 = start1Hour * 60 + start1Minute;
        const end1 = end1Hour * 60 + end1Minute;
        const start2 = start2Hour * 60 + start2Minute;
        const end2 = end2Hour * 60 + end2Minute;

        // Check for overlap
        if ((start1 < end2 && end1 > start2) || (start2 < end1 && end2 > start1)) {
          overlaps.push({
            period1: period1.label,
            period2: period2.label,
            message: `${period1.label} (${period1.startTime}-${period1.endTime}) overlaps with ${period2.label} (${period2.startTime}-${period2.endTime})`
          });
        }
      }
    }

    return overlaps;
  }

  /**
   * Calculate total duration of all periods in minutes
   */
  calculateDuration(periods) {
    if (!periods || periods.length === 0) {
      return 0;
    }

    let totalMinutes = 0;
    periods.forEach(period => {
      const [startHour, startMinute] = period.startTime.split(':').map(Number);
      const [endHour, endMinute] = period.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      totalMinutes += (endMinutes - startMinutes);
    });

    return totalMinutes;
  }

  /**
   * Helper: Generate template ID from name
   */
  generateTemplateId(name) {
    return name.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  /**
   * Helper: Validate time format (HH:MM)
   */
  isValidTime(time) {
    const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return regex.test(time);
  }

  /**
   * Helper: Validate color format (#RRGGBB)
   */
  isValidColor(color) {
    const regex = /^#[0-9A-Fa-f]{6}$/;
    return regex.test(color);
  }

  /**
   * Get default templates (for reference)
   */
  getDefaultTemplates() {
    return DEFAULT_TEMPLATES;
  }
}

// Export singleton instance
export const dayTemplatesService = new DayTemplatesService();
